import { useMutation } from "@tanstack/react-query";

import { useAxios, useApiResponseParser } from "../";

export default function useDocument() {
    const { axiosPrivate } = useAxios();
    const { parseSuccessResponseData } = useApiResponseParser();

    function useGetContractPricing() {
        return useMutation({
            mutationFn: (parameters: {
                ProjectId: string;
                EstimateOptionId?: string;
            }) =>
                axiosPrivate
                    .post(`/v1/document/contract-pricing`, parameters)
                    .then(parseSuccessResponseData),
        });
    }

    function useGenerateContract() {
        return useMutation({
            mutationFn: (parameters: {
                ProjectId: string;
                EstimateOptionId?: string;
            }) =>
                axiosPrivate
                    .post(`/v1/document/generate-contract`, parameters)
                    .then(parseSuccessResponseData),
        });
    }

    function usePreviewContract() {
        return useMutation({
            mutationFn: async (parameters: {
                ProjectId: string;
                EstimateOptionId?: string;
            }) => {
                try {
                    const response = await axiosPrivate.post(
                        `/v1/document/preview-contract`,
                        parameters,
                        { responseType: "blob" },
                    );
                    const contentType = String(
                        response.headers["content-type"] || "",
                    );
                    if (!contentType.includes("application/pdf")) {
                        const text = await (response.data as Blob).text();
                        let message = "Failed to render contract preview PDF";
                        try {
                            const parsed = JSON.parse(text);
                            message =
                                parsed?.Message ||
                                parsed?.message ||
                                parsed?.data ||
                                message;
                        } catch {
                            /* ignore */
                        }
                        throw new Error(
                            typeof message === "string"
                                ? message
                                : "Failed to render contract preview PDF",
                        );
                    }
                    const blob = new Blob([response.data], {
                        type: "application/pdf",
                    });
                    return {
                        blob,
                        blobUrl: URL.createObjectURL(blob),
                        generatorKey:
                            (response.headers["x-contract-generator-key"] as
                                | string
                                | undefined) || undefined,
                    };
                } catch (err: any) {
                    if (err?.response?.data instanceof Blob) {
                        try {
                            const text = await err.response.data.text();
                            const parsed = JSON.parse(text);
                            const message =
                                parsed?.Message ||
                                parsed?.message ||
                                parsed?.data;
                            if (typeof message === "string" && message) {
                                throw new Error(message);
                            }
                        } catch (inner) {
                            if (inner instanceof Error && inner.message) {
                                throw inner;
                            }
                        }
                    }
                    throw err instanceof Error
                        ? err
                        : new Error(
                              err?.message ||
                                  "Failed to render contract preview PDF",
                          );
                }
            },
        });
    }

    function useTriggerContractSigning() {
        return useMutation({
            mutationFn: (parameters: { ProjectId: string; ToAddress: string; IsEmbeddedSigning?: boolean }) =>
                axiosPrivate.post(`/v1/document/trigger-contract-signing`, parameters).then(parseSuccessResponseData),
        });
    }

    function useGetEmbeddedSignLink() {
        return useMutation({
            mutationFn: (parameters: { DocumentId: string; SignerEmail: string; RedirectUrl?: string }) =>
                axiosPrivate
                    .get(`/v1/document/${parameters.DocumentId}/sign-link`, { params: parameters })
                    .then(parseSuccessResponseData),
        });
    }

    function useSupplyOrderCreate() {
        return useMutation({
            mutationFn: (parameters: any) =>
                axiosPrivate.post(`/v1/document/supply-order`, parameters).then(parseSuccessResponseData),
        });
    }

    function useHouseOwnerInvoiceCreate() {
        return useMutation({
            mutationFn: (parameters: any) =>
                axiosPrivate.post(`/v1/document/house-owner-invoice`, parameters).then(parseSuccessResponseData),
        });
    }

    function useInitialInspectionCreate() {
        return useMutation({
            mutationFn: (parameters: { ProjectId: string }) =>
                axiosPrivate.post(`/v1/document/initial-inspection`, parameters).then(parseSuccessResponseData),
        });
    }

    function useInitialInspectionPhotoReportCreate() {
        return useMutation({
            mutationFn: (parameters: { ProjectId: string }) =>
                axiosPrivate
                    .post(`/v1/document/initial-inspection-photo-report`, parameters)
                    .then(parseSuccessResponseData),
        });
    }

    function useClaimsEstimateCreate() {
        return useMutation({
            mutationFn: (parameters: { ProjectId: string }) =>
                axiosPrivate.post(`/v1/document/claims-estimate`, parameters).then(parseSuccessResponseData),
        });
    }

    function useInitialCreate() {
        return useMutation({
            mutationFn: (parameters: any) =>
                axiosPrivate.post(`/v1/document/create`, parameters).then(parseSuccessResponseData),
        });
    }

    function useList() {
        return useMutation({
            mutationFn: (params: any) =>
                axiosPrivate.get(`/v1/document/list`, { params }).then(parseSuccessResponseData),
        });
    }

    function useUpdateStatus() {
        return useMutation({
            mutationFn: (parameters: { DocumentId: string; Status: string }) =>
                axiosPrivate.post(`/v1/document/status/update`, parameters).then(parseSuccessResponseData),
        });
    }

    function useWorkOrderCreate() {
        return useMutation({
            mutationFn: (parameters: { ProjectId: string }) =>
                axiosPrivate.post(`/v1/document/work-order`, parameters).then(parseSuccessResponseData),
        });
    }

    function useGutterOrderCreate() {
        return useMutation({
            mutationFn: (parameters: { ProjectId: string }) =>
                axiosPrivate.post(`/v1/document/gutter-order`, parameters).then(parseSuccessResponseData),
        });
    }

    return {
        useGetContractPricing,
        useGenerateContract,
        usePreviewContract,
        useTriggerContractSigning,
        useList,
        useInitialCreate,
        useSupplyOrderCreate,
        useHouseOwnerInvoiceCreate,
        useInitialInspectionCreate,
        useInitialInspectionPhotoReportCreate,
        useClaimsEstimateCreate,
        useUpdateStatus,
        useWorkOrderCreate,
        useGutterOrderCreate,
        useGetEmbeddedSignLink,
    };
}
